import { useEffect } from "react"
import { observer } from "mobx-react"
import { DEFAULT_LANG, Languages } from "@providers"
import { redirect } from "@lib/isomorphic"
import {
  useStore,
  Page,
  Header,
  MainNavigation,
  Footer,
  PureLogo,
} from "@features/common"
import {
  ArticlePreviewClipPath,
  JournalLogoEN,
  JournalLogoRU,
  JournalNavigation,
  PostsGrid,
  PromoBanner,
} from "@features/journal"
import { DatingLogo } from "@features/dating"
import { scrollToTop } from "@lib/scroll"
import { IconHamburger } from "@lib/icons"
import { RouterLink } from "@lib/routing"
import { useShowSubscriptionDialog } from "@features/subscription"

// @ts-ignore
export async function getServerSideProps(context) {
  const Models = context.req.Models
  const { slug = "", lang = "en" } = context.params

  if (
    Models.GlobalModel.accessLanguages.findIndex(
      (accessLang) => accessLang === lang,
    ) === -1 ||
    // @ts-ignore
    !Languages[lang]
  ) {
    if (Models.GlobalModel.accessLanguages.length > 0) {
      redirect(`/${DEFAULT_LANG}`, context)
    } else if (!/not-found/i.test(context.req.url ?? "")) {
      redirect(`/${DEFAULT_LANG}/not-found`, context)
    }
    return { props: Models.serialize() }
  }

  Models.GlobalModel.setLang(lang)

  try {
    await Models.GlobalModel.getClientCountryCode(context)
    await Models.BlogModel.getPostsByCategory(slug as string)
    await Models.BlogModel.getAllCategories()
    await Models.BlogModel.getCategoriesBySlug(slug)
    return {
      props: Models.serialize(),
    }
  } catch (err) {
    return {
      redirect: {
        destination: `/${lang}/journal`,
        permanent: false,
      },
    }
  }
}

const CategoryPostsPage = observer(function CategoryPostsPage() {
  const { BlogModel, GlobalModel } = useStore()

  useShowSubscriptionDialog()

  useEffect(() => {
    document.querySelector("main")?.scrollTo({
      top: 0,
    })
  }, [BlogModel.category?.id])

  const pageTitle = BlogModel.getTemplate("title")
  const metaTitle = BlogModel.category?.metaTitle || pageTitle
  const pageDescription =
    BlogModel.category?.metaDescription ||
    BlogModel.getTemplate("metaDescription")
  const seoH1 = BlogModel.category?.seoH1

  const alternateLinks = BlogModel.relatedCategoriesBySlug.map(
    ({ lang, slug }) => ({
      lang,
      url: `${GlobalModel.originUrl || ""}/${lang}/journal/categories/${slug}`,
    }),
  )

  return (
    <Page
      meta={{
        title: pageTitle,
        description: pageDescription,
        metaTitle,
        alternateLinks,
      }}
      navigation={
        <MainNavigation>
          <JournalNavigation />
        </MainNavigation>
      }
      header={
        <Header
          theme="dark"
          logo={
            <RouterLink
              uri="/journal"
              onClick={() => scrollToTop()}
              data-test-id="header__pure_logo"
              aria-label="root"
              className="mt-4 mb-3"
            >
              <div className="flex space-x-2 tablet-m:space-x-4">
                <PureLogo className="text-white h-4 w-auto my-1 tablet-m:w-auto tablet-m:h-7 tablet:h-7 tablet-m:my-1.5" />
                {GlobalModel.isPureDating ? (
                  <DatingLogo />
                ) : (
                  <>
                    {GlobalModel.lang === "ru" ? (
                      <JournalLogoRU />
                    ) : (
                      <JournalLogoEN />
                    )}
                  </>
                )}
              </div>
            </RouterLink>
          }
          languageSwitcher={null}
          navigationOpener={
            <button
              onClick={GlobalModel.openMenu}
              data-test-id="header__menu_button"
              aria-label="menu"
            >
              <IconHamburger className="w-8 h-8 text-white" />
            </button>
          }
        />
      }
      alternativeHeader
      seoH1={seoH1}
      footer={
        <Footer
          googlePlayLink={BlogModel.branch_links.category_footer_google_play}
          appStoreLink={BlogModel.branch_links.category_footer_app_store}
          appGalleryLink={BlogModel.branch_links.category_footer_app_gallery}
        />
      }
    >
      <div className="bg-journal-background bg-no-repeat bg-[length:100%_1628px] bg-[center_-80px]">
        <ArticlePreviewClipPath />
        <div className="pt-9 tablet:pt-12">
          <PostsGrid />
        </div>
        <div className="pt-13 pb-16 tablet:pt-12 tablet:pb-19 desktop:pb-24">
          <PromoBanner />
        </div>
      </div>
    </Page>
  )
})

export default CategoryPostsPage
